import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { Badge } from '@gto/ui/badge'
import { cn } from '@gto/ui/cn'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { TruncatedText } from '@gto/ui/truncated-text'

import type { EarningDeal } from '@/api/gen'
import { fluidColumn, headerWithHint } from '@/components/table-columns'
import { CategoryColorBadge } from '@/pages/rewards/components/category-color-badge'
import { SUMMARY_ROW_ID } from '@/pages/rewards/components/earnings-summary-row'
import { EdgeTiersTooltip } from '@/pages/rewards/components/edge-tiers-tooltip'
import { categorySchema } from '@/pages/rewards/constants'

/**
 * A row is either one of the month's deals or the totals line that closes the table. They
 * differ in exactly one field: no single multiplier applies to a total spanning both bases, so
 * `edge_multiplier` is optional here and the cell renders nothing for the summary.
 */
export type EarningsRow = Omit<EarningDeal, 'edge_multiplier'> & {
  id: string
  edge_multiplier?: string
}

export const useEarningsTableCols = (): ColumnDef<EarningsRow>[] => {
  const { formatMessage, formatNumber } = useIntl()

  const isSummaryRow = (row: EarningsRow) => row.id === SUMMARY_ROW_ID

  /**
   * The deal's basis is highlighted and the other greyed, so a row shows at a glance which of
   * GGR/NGR it was paid on. The totals row is neither — it is the sum of both, so it stays in
   * primary text and only carries the bold weight.
   */
  const rakeCell = (row: EarningsRow, value: string, isBasis: boolean) => {
    // The summary keeps the cell's default colour; only a deal's own basis is tinted.
    const tone = isSummaryRow(row)
      ? 'font-semibold'
      : isBasis
        ? 'font-semibold text-primary-main'
        : 'text-text-ghost'

    return (
      <span className={tone}>
        <FormattedNumber
          currency={row.currency}
          style="currency"
          value={Number(value)}
        />
      </span>
    )
  }

  return createColumns<EarningsRow>([
    {
      accessorKey: 'poker_room_name',
      header: formatMessage({ id: 'rewards.earnings.table.pokerRoomName' }),
      ...fluidColumn({ min: 220 }),
      // `min-w-0` on the flex row is the half `TruncatedText` cannot supply: it makes ITSELF
      // shrinkable, but this wrapper also has to be, or the deficit never reaches it.
      cell: ({ row }) => {
        const category = categorySchema.safeParse(row.original.last_processed_category)

        return (
          <div className="flex min-w-0 items-center gap-4">
            {category.success && <CategoryColorBadge category={category.data} />}
            <TruncatedText
              className={cn(isSummaryRow(row.original) && 'font-semibold')}
              placement="right"
              variant="body2"
            >
              {row.original.poker_room_name}
            </TruncatedText>
          </div>
        )
      },
    },
    {
      accessorKey: 'username',
      header: formatMessage({ id: 'rewards.earnings.table.username' }),
      ...fluidColumn({ min: 120, align: 'center', headerAlign: 'center' }),
    },
    {
      accessorKey: 'ggr',
      header: formatMessage({ id: 'rewards.earnings.table.ggr' }),
      ...fluidColumn({ min: 120, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) =>
        rakeCell(row.original, row.original.ggr, row.original.edge_calculation_base === 'GGR'),
    },
    {
      accessorKey: 'ngr',
      header: formatMessage({ id: 'rewards.earnings.table.ngr' }),
      ...fluidColumn({ min: 120, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) =>
        rakeCell(row.original, row.original.ngr, row.original.edge_calculation_base === 'NGR'),
    },
    {
      accessorKey: 'edge_multiplier',
      header: headerWithHint(
        formatMessage({ id: 'rewards.earnings.table.edgeMultiplier' }),
        <FormattedMessage id="rewards.earnings.table.edgeMultiplier.tip" />,
      ),
      ...fluidColumn({ min: 180, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => {
        const { current_tier, edge_multiplier, tier_structure } = row.original

        // The totals row carries no multiplier.
        if (edge_multiplier === undefined) {
          return null
        }

        return (
          <div className="flex items-center gap-2">
            <Badge color="info">{`x ${formatNumber(Number(edge_multiplier))}`}</Badge>
            {tier_structure !== null && tier_structure !== undefined && (
              <EdgeTiersTooltip
                current_tier={current_tier}
                tier_structure={tier_structure}
              />
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'edge_earned',
      header: formatMessage({ id: 'rewards.earnings.table.edgeEarned' }),
      ...fluidColumn({ min: 120, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => (
        <span className={cn(isSummaryRow(row.original) && 'font-semibold')}>
          <FormattedNumber value={Number(row.original.edge_earned)} />
        </span>
      ),
    },
    {
      accessorKey: 'edge_credited',
      header: headerWithHint(
        formatMessage({ id: 'rewards.earnings.table.edgeCredited' }),
        <FormattedMessage id="rewards.earnings.table.edgeCredited.tip" />,
      ),
      ...fluidColumn({ min: 180, align: 'center', headerAlign: 'center' }),
      cell: ({ row }) => (
        <span className={cn(isSummaryRow(row.original) && 'font-semibold')}>
          <FormattedNumber value={Number(row.original.edge_credited)} />
        </span>
      ),
    },
  ])
}
